import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertCircle, ArrowRight, Loader2, KeyRound } from 'lucide-react';
import { adminApi, useAdminAuth } from './AdminAuthContext';

/**
 * Two-step admin login:
 *   Step 1: email + password → backend returns session_token (5min)
 *   Step 2: 6-digit TOTP code → backend sets httpOnly cookies, we redirect
 *
 * Recovery fallback link toggles step 2 into a recovery-code input.
 */
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();
  const [stage, setStage] = useState('credentials'); // credentials | otp | recovery
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [otp, setOtp] = useState('');
  const [recovery, setRecovery] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = await adminApi.post('/admin/login', { email, password });
      setSessionToken(data.session_token);
      setStage('otp');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError(null);
    if (otp.length !== 6) { setError('6-stelliger Code erforderlich.'); return; }
    setBusy(true);
    try {
      await adminApi.post('/admin/login/verify', { session_token: sessionToken, totp_code: otp });
      await refresh();
      navigate('/admin/leads', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Code falsch.');
    } finally { setBusy(false); }
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await adminApi.post('/admin/login/recovery', { session_token: sessionToken, recovery_code: recovery.trim() });
      await refresh();
      navigate('/admin/leads', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Recovery-Code ungültig.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E0D2] p-7 lg:p-9 shadow-[0_20px_50px_-25px_rgba(31,29,26,0.3)]">
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <p className="font-display text-[20px] leading-none">Admin-Anmeldung</p>
            <p className="text-[12px] text-[#5C5852] mt-1">horrenmaker.nl</p>
          </div>
        </div>

        {stage === 'credentials' && (
          <form onSubmit={submitCredentials} data-testid="admin-login-form">
            <label className="block mb-4">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">E-Mail</span>
              <input type="email" required autoFocus autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]" />
            </label>
            <label className="block mb-5">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Passwort</span>
              <input type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]" />
            </label>
            {error && <p data-testid="admin-login-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-login-submit"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Weiter
            </button>
            <p className="mt-6 text-[11.5px] text-[#5C5852]">
              Noch nicht eingerichtet? <Link to="/" className="underline">Zurück zur Webseite</Link>
            </p>
          </form>
        )}

        {stage === 'otp' && (
          <form onSubmit={submitOtp} data-testid="admin-login-otp-form">
            <p className="text-[13px] text-[#5C5852] mb-4">Öffne deine Authenticator-App und gib den 6-stelligen Code ein.</p>
            <label className="block mb-4">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">6-stelliger Code</span>
              <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus autoComplete="one-time-code"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="admin-login-otp"
                className="w-full px-3 py-3 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[24px] font-mono text-center tabular-nums tracking-[0.3em]" />
            </label>
            {error && <p data-testid="admin-login-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
            <button type="submit" disabled={busy || otp.length !== 6} data-testid="admin-login-verify"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Anmelden
            </button>
            <p className="mt-5 text-[11.5px] text-[#5C5852]">
              Smartphone weg? <button type="button" onClick={() => { setError(null); setStage('recovery'); }} data-testid="admin-login-switch-recovery"
                className="underline inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Recovery-Code verwenden</button>
            </p>
          </form>
        )}

        {stage === 'recovery' && (
          <form onSubmit={submitRecovery} data-testid="admin-login-recovery-form">
            <p className="text-[13px] text-[#5C5852] mb-4">Gib einen deiner Recovery-Codes ein. Jeder Code funktioniert nur einmal.</p>
            <label className="block mb-4">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Recovery-Code</span>
              <input type="text" autoFocus
                value={recovery} onChange={(e) => setRecovery(e.target.value)}
                data-testid="admin-login-recovery"
                placeholder="xxxxx-xxxxx"
                className="w-full px-3 py-3 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[16px] font-mono text-center tabular-nums" />
            </label>
            {error && <p data-testid="admin-login-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-login-recovery-submit"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Anmelden
            </button>
            <p className="mt-5 text-[11.5px] text-[#5C5852]">
              <button type="button" onClick={() => { setError(null); setStage('otp'); }} className="underline">Zurück zum 6-Code</button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
